import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import {
  Button,
  Empty,
  ErrorBlock,
  Input,
  OrderStatus,
  Spinner,
} from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { orgFetch } from "../../api/org-client.ts";
import { useIdentities } from "../../api/queries.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 要我发的货
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **发货的动作是"照着收货信息填单号"，两边要并排。**
 *    手机上只能上下堆：看一眼地址、往下滚、填单号、再滚回去核对。
 *    网页上左边是这一单的货和收货信息、右边就是填单号的框 ——
 *    眼睛不用来回跑，也不会填串行。
 *
 * ⚠️ 这个列表按 `shipBy` 决定查谁的单（供应商直发 / 卖家自己备货发货），
 *    具体归谁**由服务端判**，前端不复制这条规则 —— 两处迟早走岔。
 */

interface FulfillOrder {
  id: number;
  orderNo: string;
  payment: string;
  fulfillment: string;
  shipName: string;
  shipPhone: string;
  shipAddress: Record<string, string>;
  shippingCarrier?: string | null;
  trackingNo?: string | null;
  createdAt: string;
  items: {
    id: number;
    titleSnapshot: string;
    specsSnapshot: Record<string, string> | null;
    quantity: number;
  }[];
}

/** 发货表单。⚠️ 承运商和单号都必填 —— 少一个买家就查不到物流。 */
function ShipBox({
  orderId,
  orgId,
  onDone,
}: {
  orderId: number;
  orgId: number;
  onDone: () => void;
}) {
  const [carrier, setCarrier] = useState("");
  const [trackingNo, setTrackingNo] = useState("");

  const ship = useMutation({
    mutationFn: () =>
      orgFetch(`/supply-orders/${orderId}/ship`, orgId, {
        method: "POST",
        body: JSON.stringify({ carrier, trackingNo }),
      }),
    onSuccess: onDone,
  });

  return (
    <div className="space-y-2.5">
      <p className="text-[13px] font-medium">填运单号</p>
      <Input placeholder="承运商" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
      <Input
        placeholder="快递单号"
        value={trackingNo}
        onChange={(e) => setTrackingNo(e.target.value)}
      />
      {ship.error && <ErrorBlock error={ship.error} />}
      <Button
        className="w-full"
        loading={ship.isPending}
        disabled={!carrier.trim() || !trackingNo.trim()}
        onClick={() => ship.mutate()}
      >
        确认发货
      </Button>
    </div>
  );
}

export function DesktopShip() {
  const search = useSearch({ from: "/ship" }) as { orgId?: number };
  const { data: identities, isPending } = useIdentities();
  const qc = useQueryClient();
  const orgs = identities?.orgs ?? [];
  const orgId = search.orgId ?? orgs[0]?.id;

  /** 只看待发的，还是全部。⚠️ 默认只看待发 —— 他打开这一页就是来发货的。 */
  const [onlyPending, setOnlyPending] = useState(true);

  const list = useQuery({
    queryKey: ["merchant", orgId, "supply-orders", onlyPending],
    queryFn: () =>
      orgFetch<{ items: FulfillOrder[] }>(
        `/supply-orders?limit=50${onlyPending ? "&fulfillment=pending" : ""}`,
        orgId!,
      ),
    enabled: Boolean(orgId),
  });

  /**
   * 🔴 **没有商家身份时不能只转圈。** disabled 的查询 `isPending` 恒为 true ——
   *    未登录、或名下没有店的人会看到一个永远停不下来的圈。
   */
  if (!isPending && !orgId)
    return (
      <AccountShell title="要我发的货">
        <Empty title="你还不是商家" hint="发货是商家功能。想开店请联系上级商家或平台。" action={{ label: "回首页", to: "/" }} compact />
      </AccountShell>
    );
  if (isPending || list.isPending)
    return (
      <AccountShell title="要我发的货">
        <Spinner compact />
      </AccountShell>
    );
  if (list.error)
    return (
      <AccountShell title="要我发的货">
        <ErrorBlock error={list.error} />
      </AccountShell>
    );

  const items = list.data?.items ?? [];

  return (
    <AccountShell title="要我发的货">
      <div className="mb-4 flex gap-2">
        {[
          [true, "待发货"],
          [false, "全部"],
        ].map(([v, label]) => (
          <button
            key={String(v)}
            onClick={() => setOnlyPending(v as boolean)}
            className={cn(
              "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
              onlyPending === v
                ? "bg-brand-600 text-white"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground",
            )}
          >
            {label as string}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <Empty
          title={onlyPending ? "没有要发的货" : "还没有订单"}
          hint={onlyPending ? "客户下单并付款后会出现在这里" : undefined}
          compact
        />
      ) : (
        <div className="space-y-4">
          {items.map((o) => (
            <div key={o.id} className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="flex items-center gap-4 border-b border-border bg-secondary/40 px-5 py-2.5 text-[13px]">
                <span className="font-mono text-muted-foreground">{o.orderNo}</span>
                <span className="text-muted-foreground">
                  {new Date(o.createdAt).toLocaleString("zh-CN")}
                </span>
                <span className="ml-auto">
                  <OrderStatus payment={o.payment} fulfillment={o.fulfillment} />
                </span>
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)_300px]">
                {/* ── 左：发什么、发给谁 ── */}
                <div className="p-5">
                  <p className="mb-2 text-[13px] text-muted-foreground">发什么</p>
                  {o.items.map((it) => (
                    <div key={it.id} className="flex items-baseline justify-between gap-4 py-1">
                      <span className="min-w-0 text-[14px]">
                        {it.titleSnapshot}
                        {it.specsSnapshot && (
                          <span className="ml-2 text-[12px] text-muted-foreground">
                            {Object.values(it.specsSnapshot).join(" / ")}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-[14px] text-muted-foreground">
                        × {it.quantity}
                      </span>
                    </div>
                  ))}

                  {/*
                    🔴 **收货信息要完整给到发货的人。**
                       他要照着这个填快递单 —— 手机号打码、地址省略的话他根本发不出去。
                       （跟商家订单列表那边不同：那里是给他看"卖了什么"，不是发货。）
                    ⚠️ 网页上给复制按钮：填快递单是在另一个系统里，手抄地址最容易错。
                  */}
                  <div className="mt-4 rounded-lg bg-secondary/50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1 text-[14px] leading-relaxed">
                        <p>
                          {o.shipName}
                          <span className="ml-3 text-muted-foreground">{o.shipPhone}</span>
                        </p>
                        <p className="text-muted-foreground break-anywhere">
                          {o.shipAddress.province}
                          {o.shipAddress.city}
                          {o.shipAddress.district} {o.shipAddress.detail}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const text = `${o.shipName} ${o.shipPhone} ${o.shipAddress.province ?? ""}${o.shipAddress.city ?? ""}${o.shipAddress.district ?? ""} ${o.shipAddress.detail ?? ""}`;
                          void navigator.clipboard?.writeText(text).catch(() => {});
                        }}
                      >
                        <Copy className="size-3.5" />
                        复制
                      </Button>
                    </div>
                  </div>
                </div>

                {/* ── 右：填单号 ── */}
                <div className="border-l border-border p-5">
                  {o.trackingNo ? (
                    <>
                      <p className="text-[13px] text-muted-foreground">已发货</p>
                      <p className="mt-1.5 text-[14px]">{o.shippingCarrier}</p>
                      <p className="mt-0.5 font-mono text-[14px] text-muted-foreground">
                        {o.trackingNo}
                      </p>
                    </>
                  ) : o.payment === "paid" && o.fulfillment === "pending" && orgId ? (
                    /**
                     * ⚠️ 只在**已付款且待发货**时出发货框。
                     *    未付款就给输入框的话，他填了也发不出去（服务端会拒）——
                     *    看得见、点得动、就是不成，是最糟的交互。
                     */
                    <ShipBox
                      orderId={o.id}
                      orgId={orgId}
                      onDone={() =>
                        qc.invalidateQueries({ queryKey: ["merchant", orgId, "supply-orders"] })
                      }
                    />
                  ) : (
                    <p className="text-[13px] leading-relaxed text-muted-foreground">
                      {o.payment !== "paid" ? "等客户付款后才能发货。" : "这一单不用你发。"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AccountShell>
  );
}
